import { Attendance, AttendanceForm } from '../types/Attendance';
import { toCamelCase, toSnakeCase } from '../utils/caseConverter';
import { getApiHeaders } from '../utils/apiHeaders';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ymdLocal = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const fetchAttendances = (params?: { 
  date?: string; 
  userId?: number; 
  startDate?: string; 
  endDate?: string;
  includeTeam?: boolean; // For managers to include team attendance
}) => {
  const queryParams = new URLSearchParams();
  if (params?.date) queryParams.append('date', params.date);
  if (params?.userId) queryParams.append('user_id', params.userId.toString());
  if (params?.startDate) queryParams.append('start_date', params.startDate);
  if (params?.endDate) queryParams.append('end_date', params.endDate);
  if (params?.includeTeam) queryParams.append('include_team', 'true');

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/api/attendances${queryString ? `?${queryString}` : ''}`;

  return fetch(url, {
    headers: getApiHeaders('none')
  }).then(res => res.json()).then(data => toCamelCase(data));
};

export async function createAttendance(data: AttendanceForm): Promise<Attendance> {
  const formData = new FormData();
  
  // Add all form fields
  formData.append('user_id', data.userId.toString());
  formData.append('date', data.date);
  if (data.checkInTime) formData.append('check_in_time', data.checkInTime);
  if (data.checkOutTime) formData.append('check_out_time', data.checkOutTime);
  formData.append('status', data.status);
  if (data.latitude !== undefined) formData.append('latitude', data.latitude.toString());
  if (data.longitude !== undefined) formData.append('longitude', data.longitude.toString());
  
  // Add selfie file if it's a File object
  if (data.selfie instanceof File) {
    formData.append('selfie', data.selfie);
  } else if (data.selfie && typeof data.selfie === 'string') {
    // If it's a URL string, we might need to fetch and convert it
    // For now, just skip if it's already a URL (existing image)
  }

  const res = await fetch(`${API_BASE_URL}/api/attendances`, {
    method: 'POST',
    headers: getApiHeaders('form-data'),
    body: formData,
  });

  if (!res.ok) {
    const errorText = await res.text();
    // Check for conflict (409) - attendance already exists
    if (res.status === 409) {
      throw new Error('Attendance already exists for this user and date. You can only mark attendance once per day.');
    }
    throw new Error(`Failed to create attendance: ${errorText}`);
  }

  return res.json().then(data => toCamelCase(data));
}

export async function updateAttendance(id: number, data: Partial<AttendanceForm>): Promise<Attendance> {
  // For updates, check if we have a file to upload
  const hasFile = data.selfie instanceof File || data.checkoutSelfie instanceof File;
  
  let body: FormData | string;
  let headers: HeadersInit;
  
  if (hasFile) {
    // Use FormData if there's a file
    const formData = new FormData();
    if (data.userId !== undefined) formData.append('user_id', data.userId.toString());
    if (data.date) formData.append('date', data.date);
    if (data.checkInTime) formData.append('check_in_time', data.checkInTime);
    if (data.checkOutTime) formData.append('check_out_time', data.checkOutTime);
    if (data.status) formData.append('status', data.status);
    if (data.latitude !== undefined) formData.append('latitude', data.latitude.toString());
    if (data.longitude !== undefined) formData.append('longitude', data.longitude.toString());
    if (data.selfie instanceof File) {
      formData.append('selfie', data.selfie);
    }
    if (data.checkoutSelfie instanceof File) {
      formData.append('checkout_selfie', data.checkoutSelfie);
    }
    body = formData;
    headers = getApiHeaders('none');
  } else {
    // Use JSON if no file
    body = JSON.stringify(toSnakeCase(data));
    headers = getApiHeaders('json');
  }

  const res = await fetch(`${API_BASE_URL}/api/attendances/${id}`, {
    method: 'PUT',
    headers,
    body,
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to update attendance: ${errorText}`);
  }

  return res.json().then(data => toCamelCase(data));
}

export async function checkoutAttendance(
  attendanceId: number,
  checkoutSelfie: File,
  checkOutTime?: string
): Promise<Attendance> {
  const formData = new FormData();
  formData.append('checkout_selfie', checkoutSelfie);
  if (checkOutTime) formData.append('check_out_time', checkOutTime);

  const res = await fetch(`${API_BASE_URL}/api/attendances/${attendanceId}/checkout`, {
    method: 'PUT',
    headers: getApiHeaders('form-data'),
    body: formData,
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to check out: ${errorText}`);
  }

  return res.json().then((data) => toCamelCase(data));
}

export const fetchTodayAttendance = (userId?: number): Promise<Attendance[]> => {
  const today = ymdLocal(new Date());
  return fetchAttendances({ date: today, userId });
};
